import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Award, Users, Clock, Shield, Phone } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const About = () => {
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true); // Start visible

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/company-info/`);
        setCompanyInfo(response.data);
      } catch (error) {
        setCompanyInfo({
          name: "J CLASS MOTORS",
          phone: "058-7019669",
          email: "Jeromecarss@gmail.com",
          address: "תל אביב, ישראל",
          workingHours: "ראשון-חמישי: 09:00-18:00",
          whatsapp: "058-7019669"
        });
      } finally {
        setLoading(false);
      }
    };
    fetchCompanyInfo();
  }, []);

  const achievements = [
    { icon: Award, number: "15+", label: "שנות ניסיון", desc: "מומחיות ברכבים" },
    { icon: Users, number: "500+", label: "לקוחות מרוצים", desc: "בכל רחבי הארץ" },
    { icon: Clock, number: "24/7", label: "זמינות מלאה", desc: "שירות ויעוץ" },
    { icon: Shield, number: "100%", label: "שקיפות מלאה", desc: "ללא הפתעות" }
  ];

  const handleWhatsApp = () => {
    const phoneNumber = (companyInfo?.whatsapp || "0587019669").replace(/[^0-9]/g, '');
    window.open(`https://wa.me/972${phoneNumber.substring(1)}`, '_blank');
  };

  if (loading) {
    return (
      <section className="py-24 bg-luxury-black">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.p animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.5, repeat: Infinity }} className="text-luxury-gold text-xl">
            טוען...
          </motion.p>
        </div>
      </section>
    );
  }

  return (
    <section id="about" ref={sectionRef} className="py-24 bg-luxury-black relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-luxury-gold/5 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-12 bg-luxury-gold" />
            <span className="text-luxury-gold text-sm font-medium tracking-[0.3em] uppercase">הסיפור שלנו</span>
            <div className="h-px w-12 bg-luxury-gold" />
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-luxury-text mb-6">
            אודות <span className="gold-shimmer">J CLASS MOTORS</span>
          </h2>
        </motion.div>

        {/* Stats Grid */}
        <motion.div 
          className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-20"
          initial={{ opacity: 0, y: 40 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {achievements.map((item, index) => (
            <motion.div
              key={index}
              className="text-center p-6 bg-luxury-surface border border-luxury-border hover:border-luxury-gold/50 transition-all duration-500 group"
              initial={{ opacity: 0, y: 20 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <motion.div 
                className="w-14 h-14 mx-auto mb-4 border border-luxury-gold/30 flex items-center justify-center group-hover:bg-luxury-gold group-hover:border-luxury-gold transition-all duration-300"
                whileHover={{ rotate: 12 }}
              >
                <item.icon className="w-6 h-6 text-luxury-gold group-hover:text-luxury-black transition-colors duration-300" />
              </motion.div>
              <p className="text-3xl md:text-4xl font-bold text-luxury-gold mb-1">{item.number}</p>
              <p className="text-luxury-text font-medium mb-1">{item.label}</p>
              <p className="text-luxury-muted text-sm">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-luxury-text mb-6">
              מחויבות <span className="text-luxury-gold">למצוינות</span>
            </h3>
            <div className="space-y-4 text-luxury-muted leading-relaxed">
              <p>
                ב-J CLASS MOTORS אנחנו מאמינים שכל לקוח מגיע לחוויית שירות יוצאת דופן. עם ניסיון של מעל 15 שנה בתחום, אנו מתמחים באיתור רכבים איכותיים שמתאימים בדיוק לצרכים ולתקציב שלכם.
              </p>
              <p>
                הצוות המקצועי שלנו מלווה אתכם בכל שלב - מהייעוץ הראשוני, דרך בחירת הרכב המושלם ועד לשירות לאחר המכירה. אצלנו, השקיפות והיושרה הן ערכים עליונים.
              </p>
            </div>

            {/* CTA */}
            <motion.button
              onClick={handleWhatsApp}
              className="btn-luxury mt-8 flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Phone className="w-4 h-4" />
              <span>דברו איתנו עכשיו</span>
            </motion.button>
          </motion.div>

          {/* Image/Visual */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 30 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="aspect-[4/3] bg-luxury-surface border border-luxury-border overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_carshop-upgrade/artifacts/53ufsen1_Car%20Dealership%20with%20Audi.png"
                alt="J CLASS MOTORS"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Decorative corners */}
            <div className="absolute -top-3 -right-3 w-24 h-24 border-t-2 border-r-2 border-luxury-gold" />
            <div className="absolute -bottom-3 -left-3 w-24 h-24 border-b-2 border-l-2 border-luxury-gold" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
